import React, { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { ResponseArtifactDTO } from '@dreamai/types';
import TransactionalSheet, { SheetPresentation } from './TransactionalSheet';
import { WalletIcon } from './transactional-icons';
import FileLink from '../../shared/FileLink';
import {
  ExpenseReviewItem,
  ExpenseReviewUserResponse,
  buildExpenseReviewSubmitResponse,
  expensePaymentMethodKey,
  expenseStatusKey,
  formatExpenseDate,
  formatExpenseMoney,
  isExpenseSubmittable,
  readExpenseReviewItems,
} from './expenseReviewArtifact';
import './response-artifact-modal.scss';
import './expense-review-modal.scss';

/**
 * The review page behind the WhatsApp "Review" button and the inline card in
 * the web chat: each expense waiting to be filed, reduced to what a lay person
 * checks — what it was, the receipt, the amount, the date and how it was paid.
 * Account codes, tax and bank-account coding stay behind the scenes. Viewing is
 * read-only; only the Submit button records anything.
 */

interface ExpenseReviewModalProps {
  show: boolean;
  artifact: ResponseArtifactDTO;
  onSubmit: (response: ExpenseReviewUserResponse) => Promise<void>;
  onClose?: () => void;
  readOnly?: boolean;
  presentation?: SheetPresentation;
  /**
   * Share token of the public `/artifact/:shareToken` page. When set, receipts
   * are loaded through the share-token-scoped public receipt route instead of
   * the authenticated file API, so logged-out reviewers see them too.
   */
  shareToken?: string;
}

export function publicReceiptUrl(
  shareToken: string,
  fileRef: string,
): string | null {
  const match = /^\/api\/files\/(file-[\w-]+)$/.exec(fileRef);
  if (!match) return null;
  const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  return `${base}/public/artifacts/${encodeURIComponent(shareToken)}/receipts/${match[1]}`;
}

function isImage(contentType: string | null): boolean {
  return contentType !== null && contentType.startsWith('image/');
}

function Receipts({
  item,
  canOpen,
  shareToken,
}: {
  item: ExpenseReviewItem;
  canOpen: boolean;
  shareToken?: string;
}) {
  const { t } = useTranslation();
  if (!item.receipts.length)
    return (
      <p className='expense-no-receipt'>{t('expenseReview.receipts.none')}</p>
    );
  return (
    <ul className='expense-receipts'>
      {item.receipts.map((receipt, i) => {
        const name =
          receipt.filename ||
          t('expenseReview.receipts.fallbackName', { n: i + 1 });
        const publicUrl = shareToken
          ? publicReceiptUrl(shareToken, receipt.fileRef)
          : null;
        if (publicUrl && isImage(receipt.contentType)) {
          return (
            <li key={`${receipt.fileRef}-${i}`}>
              <a
                className='expense-receipt-link'
                href={publicUrl}
                target='_blank'
                rel='noopener noreferrer'
              >
                <img
                  className='expense-receipt-preview'
                  src={publicUrl}
                  alt={name}
                />
              </a>
            </li>
          );
        }
        return (
          <li key={`${receipt.fileRef}-${i}`} className='expense-receipt-file'>
            {publicUrl ? (
              <a
                className='expense-receipt-link'
                href={publicUrl}
                target='_blank'
                rel='noopener noreferrer'
              >
                {name}
              </a>
            ) : canOpen ? (
              <FileLink url={receipt.fileRef}>{name}</FileLink>
            ) : (
              <span title={t('expenseReview.receipts.openInDream')}>
                {name}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

const ExpenseReviewModal: React.FC<ExpenseReviewModalProps> = ({
  show,
  artifact,
  onSubmit,
  onClose,
  readOnly = false,
  presentation = 'modal',
  shareToken,
}) => {
  const { isAuthenticated } = useAuth0();
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const items = useMemo(() => readExpenseReviewItems(artifact), [artifact]);
  const submittable = useMemo(
    () => items.filter(isExpenseSubmittable),
    [items],
  );
  const [selected, setSelected] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSelected(submittable.map((item) => item.expenseId));
  }, [submittable]);

  const actioned = artifact.status === 'actioned';
  const locked = readOnly || actioned;
  const multi = items.length > 1;

  const toggle = (expenseId: string) => {
    if (locked || submitting) return;
    setSelected((prev) =>
      prev.includes(expenseId)
        ? prev.filter((id) => id !== expenseId)
        : [...prev, expenseId],
    );
  };

  const handleSubmit = async () => {
    if (locked || submitting || !selected.length) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(buildExpenseReviewSubmitResponse(selected, submittable));
    } catch {
      setError(t('expenseReview.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const heading = t(
    multi ? 'expenseReview.headingMulti' : 'expenseReview.headingSingle',
  );
  const sub = actioned
    ? t(
        multi
          ? 'expenseReview.alreadySubmittedMulti'
          : 'expenseReview.alreadySubmittedSingle',
      )
    : t(multi ? 'expenseReview.subMulti' : 'expenseReview.subSingle');

  const submitLabel = submitting
    ? t('expenseReview.submitting')
    : !multi
      ? t('expenseReview.submit')
      : selected.length === submittable.length
        ? t('expenseReview.submitAll', { count: submittable.length })
        : t('expenseReview.submitSome', {
            selected: selected.length,
            total: submittable.length,
          });

  const footer = locked ? (
    onClose ? (
      <button type='button' className='tx-btn-primary' onClick={onClose}>
        {t('expenseReview.close')}
      </button>
    ) : undefined
  ) : (
    <>
      <button
        type='button'
        className='tx-btn-primary'
        onClick={handleSubmit}
        disabled={submitting || !selected.length}
        data-testid='expense-review-submit'
      >
        {submitLabel}
      </button>
      {onClose && (
        <button
          type='button'
          className='tx-btn-text'
          onClick={onClose}
          disabled={submitting}
        >
          {t('expenseReview.notNow')}
        </button>
      )}
    </>
  );

  return (
    <TransactionalSheet
      show={show}
      presentation={presentation}
      onClose={onClose}
      holdOpen={submitting}
      leading={<WalletIcon />}
      ariaLabelledBy='expense-review-title'
      className='expense-review-modal'
      footer={footer}
    >
      <div className='tx-head'>
        <h1 className='tx-title' id='expense-review-title'>
          {heading}
        </h1>
        <p className='tx-sub'>{sub}</p>
      </div>

      {items.length === 0 ? (
        <p className='expense-empty' role='status'>
          {t('expenseReview.empty')}
        </p>
      ) : (
        <ul className='expense-list'>
          {items.map((item) => {
            const canSubmit = isExpenseSubmittable(item);
            const ticked = selected.includes(item.expenseId);
            const amount = formatExpenseMoney(
              item.amount,
              item.currency,
              locale,
            );
            const settled = formatExpenseMoney(
              item.settledAmount,
              item.settledCurrency,
              locale,
            );
            const paymentMethodKey = expensePaymentMethodKey(
              item.paymentMethod,
            );
            const showStatus =
              item.status !== 'draft' &&
              item.status !== 'awaiting_confirmation' &&
              item.status !== 'approved';
            const facts = [
              formatExpenseDate(item.transactionDate, locale),
              paymentMethodKey ? t(paymentMethodKey) : null,
            ].filter((fact): fact is string => Boolean(fact));
            return (
              <li
                key={item.expenseId}
                className={`expense-card${ticked ? '' : ' expense-card--unticked'}`}
                data-testid={`expense-card-${item.expenseId}`}
              >
                <div className='expense-card-head'>
                  {multi && canSubmit && !locked ? (
                    <input
                      type='checkbox'
                      className='expense-tick'
                      checked={ticked}
                      onChange={() => toggle(item.expenseId)}
                      disabled={submitting}
                      aria-label={t('expenseReview.include', {
                        vendor: item.vendor || t('expenseReview.thisExpense'),
                      })}
                    />
                  ) : null}
                  <div className='expense-card-title'>
                    <span className='expense-vendor'>
                      {item.vendor || t('expenseReview.unknownVendor')}
                    </span>
                    {item.description ? (
                      <span className='expense-purpose'>
                        {item.description}
                      </span>
                    ) : null}
                  </div>
                  {showStatus ? (
                    <span
                      className={`expense-status expense-status--${item.status}`}
                    >
                      {t(expenseStatusKey(item.status))}
                    </span>
                  ) : null}
                </div>

                {item.duplicateOfExpenseId && (
                  <p className='expense-duplicate' role='note'>
                    {t('expenseReview.duplicate', {
                      ref: item.duplicateOfExpenseId,
                    })}
                  </p>
                )}

                <Receipts
                  item={item}
                  canOpen={isAuthenticated}
                  shareToken={shareToken}
                />

                <div className='expense-amount-block'>
                  <span className='expense-amount'>
                    {amount || t('expenseReview.amountMissing')}
                  </span>
                  {settled && settled !== amount ? (
                    <span className='expense-settled'>
                      {t('expenseReview.rows.settledOnCard', {
                        amount: settled,
                      })}
                    </span>
                  ) : null}
                </div>

                {facts.length ? (
                  <p className='expense-facts'>{facts.join(' · ')}</p>
                ) : null}

                {item.providerUrl ? (
                  <p className='expense-facts'>
                    <a
                      href={item.providerUrl}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      {t('expenseReview.rows.inYourAccounts')}
                    </a>
                  </p>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {error && (
        <div className='expense-error' role='alert'>
          {error}
        </div>
      )}
    </TransactionalSheet>
  );
};

export default ExpenseReviewModal;
